import zxcvbn from 'zxcvbn'
import { z } from 'zod'
import { ErrorsState, FormValues } from '.'
import { codeValidation } from '@/helpers/validateAuthCode'
import { TFunction } from 'i18next'

// The minimum number of password strength rules that we require
// for the password to be considered strong enough.
const MIN_PASSWORD_SCORE = 3

const MIN_PASSWORD_LENGTH = 8

export function passwordStrengthScore(password: string) {
  return password ? zxcvbn(password.toString()).score : 0
}

function passwordStrengthMinScore(password: string) {
  const score = passwordStrengthScore(password)

  return score >= MIN_PASSWORD_SCORE
}

const passwordStrengthRules = [
  {
    // This is the fallback text until we are sure that the label is valid.
    text: '8 characters minimum',
    label: 'authentication:passwordStrengthRules.minimumLength',
    validator: (password: string) => password?.length >= MIN_PASSWORD_LENGTH,
  },
  {
    // This is the fallback text until we are sure that the label is valid.
    text: 'At least one number',
    label: 'authentication:passwordStrengthRules.oneNumber',
    validator: (password: string) => /\d/.test(password),
  },
  {
    // This is the fallback text until we are sure that the label is valid.
    text: 'Upper and lower case letters',
    label: 'authentication:passwordStrengthRules.uppercaseAndLowercase',
    validator: (password: string) => /[a-z]/.test(password) && /[A-Z]/.test(password),
  },
  {
    // This is the fallback text until we are sure that the label is valid.
    text: 'At least one special character',
    label: 'authentication:passwordStrengthRules.specialCharacter',
    validator: (password: string) => /[^A-Za-z0-9\s]/.test(password),
  },
  {
    // This is the fallback text until we are sure that the label is valid.
    text: 'At least strong or very strong',
    label: 'authentication:passwordStrengthRules.strengthStrongOrVeryStrong',
    validator: passwordStrengthMinScore,
  },
]

export function getPasswordStrengthRules(t: TFunction) {
  return passwordStrengthRules.map(({ text, label, validator }) => ({
    text: label ? t(label) : text,
    validator,
  }))
}

export type PasswordStrengthRule = ReturnType<typeof getPasswordStrengthRules>[number]

function passwordMeetsStrengthRules(password: string) {
  return passwordStrengthRules.every(({ validator }) => validator(password))
}

export const passwordStrengthWarning = (password: string) =>
  password
    ? zxcvbn(password.toString()).feedback?.warning ||
      zxcvbn(password.toString()).feedback?.suggestions[0] ||
      ''
    : ''

const validationSchema = z.object({
  username: z.string().email(),
  password: z
    .string()
    .refine(passwordStrengthMinScore, {
      message: 'Your password must be classified as strong',
    })
    .refine(passwordMeetsStrengthRules, {
      message: 'Your password must meet the minimum requirements',
    }),
  currentPassword: z.string(),
  confirmPassword: z.string(),
})

const validationCodeSchema = validationSchema.extend({
  authCode: codeValidation,
})

export const validateRules = (formData: Partial<FormValues>, requireCodeInput: boolean) => {
  const result = (requireCodeInput ? validationCodeSchema : validationSchema)
    .superRefine(({ confirmPassword, password }, ctx) => {
      if (confirmPassword !== password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Confirm password must match password',
        })
      }
    })
    .safeParse(formData)

  return {
    success: result.success,
    errors: (result.success ? {} : result.error.formErrors.fieldErrors) as ErrorsState,
  }
}
